/**
 * FullScreenLoadingWidget - Reusable full-screen loading overlay with optional progress bar
 *
 * Features:
 * - Full-screen semi-transparent overlay
 * - Loading spinner
 * - Optional message text
 * - Optional progress bar with percentage
 * - Easy show/hide/update API
 */

export class FullScreenLoadingWidget {
  private container: HTMLDivElement;
  private spinner: HTMLDivElement;
  private messageElement: HTMLDivElement;
  private progressBarContainer: HTMLDivElement | null = null;
  private progressBarFill: HTMLDivElement | null = null;
  private progressText: HTMLDivElement | null = null;
  private isVisible = false;

  constructor(private showProgressBar = false) {
    this.container = this.createContainer();
    this.spinner = this.createSpinner();
    this.messageElement = this.createMessage();

    if (showProgressBar) {
      const { container, fill, text } = this.createProgressBar();
      this.progressBarContainer = container;
      this.progressBarFill = fill;
      this.progressText = text;
      this.container.appendChild(this.progressBarContainer);
      // Hide spinner when progress bar is shown
      this.spinner.style.display = 'none';
    }

    this.container.appendChild(this.spinner);
    this.container.appendChild(this.messageElement);
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'potree-loading-overlay';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: monospace;
      touch-action: none;
    `;

    // Prevent context menu from appearing on right-click
    container.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    return container;
  }

  private createSpinner(): HTMLDivElement {
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 60px;
      height: 60px;
      border: 6px solid rgba(255, 255, 255, 0.3);
      border-top: 6px solid #b5ffa1;
      border-radius: 50%;
      animation: potree-spin 1s linear infinite;
      margin-bottom: 20px;
    `;

    // Add keyframe animation if not already present
    if (!document.getElementById('potree-spinner-keyframes')) {
      const style = document.createElement('style');
      style.id = 'potree-spinner-keyframes';
      style.textContent = `
        @keyframes potree-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    return spinner;
  }

  private createMessage(): HTMLDivElement {
    const message = document.createElement('div');
    message.style.cssText = `
      color: #fff;
      font-size: 18px;
      margin-bottom: 20px;
      text-align: center;
    `;
    message.textContent = 'Loading...';
    return message;
  }

  private createProgressBar(): { container: HTMLDivElement; fill: HTMLDivElement; text: HTMLDivElement } {
    const container = document.createElement('div');
    container.style.cssText = `
      width: 300px;
      height: 20px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 10px;
      position: relative;
    `;

    const fill = document.createElement('div');
    fill.style.cssText = `
      width: 0%;
      height: 100%;
      background: #b5ffa1;
      transition: width 0.3s ease;
      border-radius: 10px;
    `;

    const text = document.createElement('div');
    text.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #fff;
      font-size: 12px;
      font-weight: bold;
      text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
    `;
    text.textContent = '0%';

    container.appendChild(fill);
    container.appendChild(text);

    return { container, fill, text };
  }

  /**
   * Show the loading widget
   */
  show(message?: string): void {
    if (!this.container.parentElement) {
      document.body.appendChild(this.container);
    }

    if (message) {
      this.messageElement.textContent = message;
    }

    this.container.style.display = 'flex';
    this.isVisible = true;
  }

  /**
   * Hide the loading widget
   */
  hide(): void {
    this.container.style.display = 'none';
    this.isVisible = false;
  }

  /**
   * Update the message text
   */
  updateMessage(message: string): void {
    this.messageElement.textContent = message;
  }

  /**
   * Update the progress bar
   */
  updateProgress(progress: number): void {
    if (!this.progressBarFill || !this.progressText) {
      console.warn('[FullScreenLoadingWidget] Progress bar is not enabled');
      return;
    }

    const clampedProgress = Math.max(0, Math.min(100, progress));
    this.progressBarFill.style.width = `${clampedProgress}%`;
    this.progressText.textContent = `${Math.round(clampedProgress)}%`;
  }

  /**
   * Check if the widget is currently visible
   */
  get visible(): boolean {
    return this.isVisible;
  }

  /**
   * Dispose the widget and remove it from DOM
   */
  dispose(): void {
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
